import { type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMR } from './referenceTokens';

/**
 * Map tool buttons of the Géométries and Sélection panels (80×64, icon 24 + 12px label)
 * and the compact draw toolbar of the Mesures panel (3×32px, shadow).
 * Needs <UMFormStyles /> in the panel for hover / aria-pressed styles.
 */

export interface UMToolSpec {
  id: string;
  icon: string;
  label: string;
  disabled?: boolean;
}

export function UMToolButton({ tool, pressed, onClick }: { tool: UMToolSpec; pressed?: boolean; onClick?: (id: string) => void }) {
  return (
    <button
      type="button"
      className="um-tool"
      disabled={tool.disabled}
      aria-pressed={pressed}
      onClick={() => onClick?.(tool.id)}
      style={{
        width: 80, height: 64, boxSizing: 'border-box', border: 'none', background: 'transparent', padding: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 4,
        color: tool.disabled ? UMR.textDisabled : UMR.text, cursor: tool.disabled ? 'not-allowed' : 'pointer', fontFamily: UMR.font,
      }}
    >
      <div style={{ position: 'relative', lineHeight: '21px' }}>
        <UMIcon name={tool.icon} size={24} color="currentColor" style={{ display: 'inline-flex', lineHeight: 1 }} />
      </div>
      <span style={{ fontSize: 12, lineHeight: UMR.lhXs, color: tool.disabled ? UMR.textDisabled : UMR.textLabel }}>{tool.label}</span>
    </button>
  );
}

/** Bordered group of tools (`self-start inline-flex flex-col gap-2 p-3 border border-gray-200 rounded-lg`). */
export function UMToolGroup({ label, tools, activeId, onSelect }: { label?: string; tools: UMToolSpec[]; activeId?: string | null; onSelect?: (id: string) => void }) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{ alignSelf: 'flex-start', display: 'inline-flex', flexDirection: 'column', gap: 8, padding: 12, border: `1px solid ${UMR.borderSubtle}`, borderRadius: 8 }}
    >
      {label && <div style={{ fontSize: 12, lineHeight: UMR.lhXs, fontWeight: 500, color: UMR.textLabel }}>{label}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        {tools.map((t) => (
          <UMToolButton key={t.id} tool={t} pressed={activeId === t.id} onClick={(id) => onSelect?.(activeId === id ? '' : id)} />
        ))}
      </div>
    </div>
  );
}

/** Mesures draw toolbar (`draw-button-toolbar`): centred, 32×32 blue icons, grey separators, shadow. */
export function UMDrawToolbar({ tools, activeId, onSelect }: { tools: UMToolSpec[]; activeId?: string | null; onSelect?: (id: string) => void }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        role="toolbar"
        style={{
          display: 'inline-flex', background: UMR.white,
          boxShadow: 'rgba(0,0,0,0.1) 0 1px 5px 0, rgba(0,0,0,0.06) 0 2px 2px 0, rgba(0,0,0,0.06) 0 3px 1px 0',
        }}
      >
        {tools.map((t) => {
          // ASSUMPTION: active state (not observed without drawing) = filled blue button.
          const active = activeId === t.id;
          return (
            <button
              key={t.id}
              type="button"
              title={t.label}
              aria-label={t.label}
              aria-pressed={active}
              onClick={() => onSelect?.(active ? '' : t.id)}
              style={{
                width: 32, height: 32, padding: 0, border: 'none', borderRight: `1px solid ${UMR.border}`, cursor: 'pointer',
                background: active ? UMR.blue : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <UMIcon name={t.icon} size={20} color={active ? UMR.white : UMR.blue} style={{ height: 24 }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Vertical stack used by most panel bodies (`flex flex-col gap-N`). */
export function UMStack({ gap = 8, children }: { gap?: number; children: ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{children}</div>;
}
